import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("greinch_admin_token") || "");
  const [inputToken, setInputToken] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authed, setAuthed] = useState(false);

  const fetchLeads = async (tk) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND}/api/contact/leads?token=${encodeURIComponent(tk)}`);
      if (res.status === 401) {
        setError("Unauthorized · invalid token");
        setAuthed(false);
        setLeads([]);
        localStorage.removeItem("greinch_admin_token");
        setToken("");
        return;
      }
      const data = await res.json();
      setLeads(data.leads || []);
      setAuthed(true);
    } catch (e) {
      setError("Network error · " + (e.message || "unknown"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token]);

  const onLogin = (e) => {
    e.preventDefault();
    if (!inputToken.trim()) return;
    localStorage.setItem("greinch_admin_token", inputToken.trim());
    setToken(inputToken.trim());
  };

  const onLogout = () => {
    localStorage.removeItem("greinch_admin_token");
    setToken("");
    setInputToken("");
    setLeads([]);
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0A0C] text-[#F4EADE] flex items-center justify-center p-6">
        <SEO title="Admin Access" description="Restricted area." path="/admin" noindex />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/greinch-logo.png" alt="GREINCH" className="h-12 w-auto mx-auto mb-4" />
            <h1 className="text-2xl uppercase tracking-[0.28em]">Greinch Ops</h1>
            <p className="text-xs uppercase tracking-[0.24em] text-[#B9AFA6] mt-2">Collaboration Requests</p>
          </div>
          <form onSubmit={onLogin} className="rounded-lg border border-[rgba(244,234,222,0.14)] bg-[rgba(255,255,255,0.03)] p-6 space-y-4">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.24em] text-[#B9AFA6]">Access Token</span>
              <input
                type="password"
                value={inputToken}
                onChange={(e) => setInputToken(e.target.value)}
                className="mt-2 w-full px-3 py-2 rounded-md bg-[#1A1418] border border-[rgba(244,234,222,0.14)] text-[#F4EADE] focus:outline-none focus:border-[#E94B4B]"
                placeholder="Enter admin token"
                data-testid="admin-token-input"
                autoFocus
              />
            </label>
            {error && <div className="text-xs text-[#E94B4B]">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full py-2.5 rounded-md bg-[#E94B4B] hover:bg-[#B03A3A] text-[#F4EADE] uppercase tracking-[0.28em] text-xs disabled:opacity-50 transition"
            >
              {loading ? "Verifying..." : "Enter"}
            </button>
          </form>
          <div className="text-center mt-6">
            <Link to="/" className="text-[11px] uppercase tracking-[0.24em] text-[#B9AFA6] hover:text-[#F4EADE]">← Back to site</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0A0C] text-[#F4EADE] p-6">
      <SEO title="Admin Ops" description="Internal admin dashboard." path="/admin" noindex />
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src="/greinch-logo.png" alt="GREINCH" className="h-10 w-auto" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#B9AFA6]">Greinch Ops</div>
              <div className="text-lg uppercase tracking-[0.24em]">Collaboration Requests</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchLeads(token)}
              className="px-3 py-2 rounded-md border border-[rgba(244,234,222,0.14)] text-[10px] uppercase tracking-[0.28em] text-[#B9AFA6] hover:text-[#F4EADE]"
              data-testid="admin-refresh-button"
            >
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="px-3 py-2 rounded-md border border-[rgba(233,75,75,0.4)] text-[10px] uppercase tracking-[0.28em] text-[#E94B4B] hover:bg-[#E94B4B] hover:text-[#F4EADE] transition"
              data-testid="admin-logout-button"
            >
              Log out
            </button>
          </div>
        </div>

        {loading && <div className="text-center text-[#B9AFA6] py-8">Loading...</div>}

        {!loading && leads.length === 0 && (
          <div className="text-center border border-dashed border-[rgba(244,234,222,0.14)] rounded-lg p-12">
            <div className="text-xs uppercase tracking-[0.24em] text-[#B9AFA6]">No requests yet</div>
            <div className="text-sm mt-2">When someone submits the contact form, it will appear here.</div>
          </div>
        )}

        {!loading && leads.length > 0 && (
          <div className="rounded-lg border border-[rgba(244,234,222,0.14)] overflow-hidden">
            <div className="grid grid-cols-6 gap-4 px-4 py-3 bg-[rgba(255,255,255,0.04)] text-[10px] uppercase tracking-[0.28em] text-[#B9AFA6]">
              <div>Date</div>
              <div>Name</div>
              <div>Email</div>
              <div>Company</div>
              <div>Service</div>
              <div>Message</div>
            </div>
            {leads.map((lead) => (
              <div key={lead.id} className="grid grid-cols-6 gap-4 px-4 py-3 border-t border-[rgba(244,234,222,0.08)] text-sm hover:bg-[rgba(255,255,255,0.02)]" data-testid={`admin-lead-${lead.id}`}>
                <div className="text-[#B9AFA6] text-xs">{lead.created_at ? new Date(lead.created_at).toLocaleString() : "-"}</div>
                <div>{lead.name}</div>
                <div className="text-[#E94B4B]"><a href={`mailto:${lead.email}`} className="hover:underline">{lead.email}</a></div>
                <div className="text-[#B9AFA6]">{lead.company || "-"}</div>
                <div className="text-[#B9AFA6]">{lead.service_interest || "-"}</div>
                <div className="text-xs truncate" title={lead.message}>{lead.message || "-"}</div>
              </div>
            ))}
            <div className="px-4 py-3 border-t border-[rgba(244,234,222,0.08)] text-[10px] uppercase tracking-[0.28em] text-[#B9AFA6] text-right">
              {leads.length} request{leads.length !== 1 ? "s" : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
